// The routes both consoles have (spec/13 §1, spec/14): the root, staff sign-in, and the signed-in
// frame everything else sits in. Made fresh for each route tree — a route object belongs to one tree —
// so the tests can mount the organisation console and the HQ console in the same run.

import type { ApiClient } from '@clavian/api-client';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { queryKeys } from '../api/queries';
import { SignInPage } from '../auth/SignInPage';
import { meKey, WelcomePage } from '../auth/WelcomePage';
import { SignedIn } from '../shell/SignedIn';

/**
 * The search of `/sign-in` and `/sign-up`: `redirect`, back to where the visitor was going — only somewhere in
 * this console, a path and never `//host` — and `error`, what a refused Google round trip came back with
 * (`?error=<code>`, F-IAM-03): a short code, nothing else kept.
 */
export function readAuthSearch(search: Record<string, unknown>): { redirect?: string; error?: string; provider?: string } {
  const redirect = search['redirect'];
  const error = search['error'];
  const provider = search['provider'];
  return {
    // The provider this console named on a refusal's URL (`social.tsx`), so the screen can name it too.
    ...(provider === 'google' || provider === 'discord' || provider === 'steam' ? { provider } : {}),
    ...(typeof redirect === 'string' && /^\/(?![/\\])/.test(redirect) ? { redirect } : {}),
    ...(typeof error === 'string' && /^[A-Za-z_]{1,64}$/.test(error) ? { error } : {}),
  };
}

export interface RouterContext {
  readonly api: ApiClient;
  readonly queryClient: QueryClient;
}

export function createSharedRoutes(options: { readonly notFound: () => ReactNode }) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: Outlet,
    notFoundComponent: options.notFound,
  });

  const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-in',
    // Back to where the visitor was going — only somewhere in this console: a path, never `//host` — and
    // what a refused Google round trip came back with (`?error=`, F-IAM-03).
    validateSearch: readAuthSearch,
    component: SignInPage,
  });

  /** Everything past sign-in. A signed-out visitor is sent to sign in and brought back afterwards. */
  const signedInRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'signed-in',
    beforeLoad: async ({ context, location }) => {
      const session = await context.queryClient.ensureQueryData({
        queryKey: queryKeys.session,
        queryFn: () => context.api.getSession(),
      });
      if (session === null) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
      // A first sign-in with Google, Discord or Steam completes the profile before any screen (spec/07 §3.2). An
      // answer that cannot be read lets the member through rather than locking them out.
      const me = await context.queryClient
        .ensureQueryData({ queryKey: meKey, queryFn: () => context.api.getMe() })
        .catch(() => null);
      if (me?.profileComplete === false) throw redirect({ to: '/welcome', search: { redirect: location.href } });
    },
    component: SignedIn,
  });

  /** "Complete your profile": signed in, on the auth layout, outside the frame it guards. */
  const welcomeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/welcome',
    validateSearch: readAuthSearch,
    beforeLoad: async ({ context, location }) => {
      const session = await context.queryClient.ensureQueryData({
        queryKey: queryKeys.session,
        queryFn: () => context.api.getSession(),
      });
      if (session === null) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    },
    component: WelcomePage,
  });

  return { rootRoute, signInRoute, signedInRoute, welcomeRoute };
}
